import { cn } from 'helpers/cn';
import { usePlatformKeys, type ShortcutKey } from './use-platform-keys';

export type KbdTone = 'subtle' | 'bordered' | 'onAccent';

export type KbdProps = {
  keys: readonly ShortcutKey[];
  tone?: KbdTone;
  className?: string;
};

const TONES: Record<KbdTone, string> = {
  subtle: 'bg-field text-dim',
  bordered: 'border border-line text-dim',
  // On a filled accent button the hint has to read as part of the button, not as a
  // separate light chip stuck to it.
  onAccent: 'bg-white/20 text-current',
};

/**
 * A shortcut hint. Decorative only: it is hidden from assistive tech, and the control it
 * belongs to carries the same shortcut in aria-keyshortcuts, which is where a screen
 * reader expects to find it.
 */
export const Kbd = ({ keys, tone = 'subtle', className }: KbdProps) => {
  const { label } = usePlatformKeys();
  return (
    <kbd
      aria-hidden="true"
      className={cn(
        'inline-flex h-5 min-w-5 items-center justify-center rounded-sm px-1.5 font-mono text-micro leading-none font-medium',
        TONES[tone],
        className,
      )}
    >
      {label(keys)}
    </kbd>
  );
};
